import React from "react";
import DataTableSortIcon from "../DataTableSortIcon";
import DataTableRow from "../DataTableRow";
import DataTableCell from "../DataTableCell";

/**
 * DataTableHeader
 *
 * This component renders the header of the data table, displaying column headers that can be sorted.
 * Each column can be clicked to toggle sorting based on the column's configuration.
 *
 * @param {Object[]} columns - Array of column definitions. Each column should have an `id`, `header`, and a `sortable` property.
 * @param {Object} query - Current table query state, including sorting information.
 * @param {Function} setQuery - Function to update the query state, used to apply sorting changes.
 *
 * @example
 * <DataTableHeader
 *   columns={[
 *     { id: "name", header: "Name", sortable: true },
 *     { id: "age", header: "Age", sortable: false },
 *   ]}
 *   query={{ sort: { columnId: "name", direction: "asc" } }}
 *   setQuery={newQuery => console.log(newQuery)}
 * />
 */

const DataTableHeader = ({ columns, query, setQuery }) => {
  const handleSort = (columnId, sortable) => {
    if (!sortable) return;
    setQuery((prev) => {
      const currentSort = prev.sort;
      let newSort = { columnId, direction: "asc" };

      if (currentSort?.columnId === columnId) {
        if (currentSort.direction === "asc") {
          newSort.direction = "desc";
        } else if (currentSort.direction === "desc") {
          newSort = null; // clear sort
        }
      }

      return { ...prev, sort: newSort };
    });
  };

  return (
    <thead>
      <DataTableRow>
        {columns.map((col) => {
          const isSorted = query.sort?.columnId === col.id;
          const direction = query.sort?.direction || null;

          return (
            <DataTableCell
              key={col.id}
              onClick={() => handleSort(col.id, col.sortable)}
              style={{
                cursor: col.sortable ? "pointer" : "default",
                userSelect: "none",
              }}
              isHeader={true}
            >
              {col.header}
              {col.sortable && (
                <DataTableSortIcon direction={isSorted ? direction : null} />
              )}
            </DataTableCell>
          );
        })}
      </DataTableRow>
    </thead>
  );
};

export default DataTableHeader;
